import { bool2str, componentId, KeyCode, revealElements, setAttributes } from '../../util';
import { MultiPanelElement } from './multi-panel-element';

const TABS_CLASS_TITLE = 'js-tabs__title';
const TABS_CLASS_CONTENT = 'js-tabs__content';
const TABS_CLASS_TABLIST = '__tablist';

const getTabsId = id => `tabs${id}`;

/**
 * Takes a structure like
 * ```html
 * div.js-tabs
 *   h2.js-tabs__title(data-selected="true")
 *   div.js-tabs__content
 *     //- arbitrary content
 *   h2.js-tabs__title
 *   div.js-tabs__content
 * ```
 * and produces
 * ```html
 * div.js-tabs
 *   ul.<baseclass>__tablist
 *     li
 *       a
 *     li
 *       a
 *   div.js-tabs__content
 *     h2.js-tabs__title(data-selected="true")
 *     //- arbitrary content
 *   div.js-tabs__content
 *     h2.js-tabs__title
 * ```
 *
 * ## API
 * const a = new TabPanel(document.querySelector('.js-tabs'), 'tabs')
 * a.activePanel // div.js-tabs__content || null
 * a.activePanel = document.querySelectorAll('.js-tabs__content')[1]
 * a.destroy() // revert changes made by this component
 *
 * ## Keyboard-interaction
 * on buttons
 * * UP / LEFT: focus previous button
 * * DOWN / RIGHT: focus next button
 * * HOME: focus first button
 * * END: focus last button
 */
class TabPanel extends MultiPanelElement {

    /**
     * @param {HTMLDivElement} element
     * @param {string} baseclass
     */
    constructor(element, baseclass) {
        super(element);
        this._construct = this._construct.bind(this);
        this._onButtonClick = this._onButtonClick.bind(this);
        this._onButtonKey = this._onButtonKey.bind(this);
        this._focusButton = this._focusButton.bind(this);
        this._focusPreviousButton = this._focusPreviousButton.bind(this);
        this._focusNextButton = this._focusNextButton.bind(this);
        this.destroy = this.destroy.bind(this);

        /**
         * @type {HTMLDivElement}
         */
        this._element = element;
        this._baseclass = baseclass;
        /**
         * @type {HTMLAnchorElement[]}
         * @private
         */
        this._buttons = [];
        this._construct();
    }

    get activePanel() {
        return this._element.querySelector('[role=tabpanel][aria-hidden=false]');
    }

    set activePanel(element) {
        if (!this._element.contains(element)) {
            throw new ReferenceError('The given element is not part of this TabPanel');
        }
        this._openTab(element, true);
    }

    _openTab(element, targetState) {
        const panel = element;
        const title = panel.firstElementChild;
        if (element === this.activePanel) {
            if (targetState) {
                // can't change the single active tab
                return;
            }
            setAttributes(element, {
                'aria-hidden': 'true',
            });
        } else {
            if (this.activePanel !== null) {
                this._openTab(this.activePanel, false);
            }
            panel.setAttribute('aria-hidden', bool2str(!targetState));
        }

        setAttributes(title, {
            'data-selected': bool2str(targetState),
            'aria-selected': bool2str(targetState)
        });

        const button = this._element.querySelector('#' + panel.getAttribute('aria-labelledby'));
        
        setAttributes(button, {
            'data-selected': bool2str(targetState),
            'aria-selected': bool2str(targetState)
        });

        if (targetState) {
            revealElements(panel, true);
        }
    }

    _construct() {
        const ul = document.createElement('UL');
        const elId = getTabsId(componentId());
        const tabId = i => `${elId}_tab${i}`;
        const labelId = i => `${elId}_label${i}`;
        setAttributes(ul, {
            'id': elId,
            'role': 'tablist',
        });
        ul.classList.add(this._baseclass + TABS_CLASS_TABLIST);
        let tabbingEnabled = false;
        let tabOpened = false;
        Array.from(this._element.querySelectorAll('.' + TABS_CLASS_TITLE)).forEach((title, i) => {
            let panel = title.nextElementSibling;
            const isPanel = p => p.classList && p.classList.contains(TABS_CLASS_CONTENT);
            while (!isPanel(panel)) {
                panel = panel.nextElementSibling;
            }
            if (panel === null || !isPanel(panel)) {
                throw new ReferenceError(`Accordion element ${i + 1} has no adjacent content`);
            }
            const isSelected = title.dataset.selected === 'true';
            const li = document.createElement('LI');
            li.setAttribute('role', 'presentation');
            const a = document.createElement('A');
            setAttributes(a, {
                'aria-controls': tabId(i),
                'id': labelId(i),
                'role': 'tab',
                'tabindex': '-1',
                'class': 'tab__link',
                'aria-selected': 'false'
            });
            a.innerHTML = (title.firstChild || title).innerHTML;
            a.addEventListener('click', this._onButtonClick);
            a.addEventListener('keydown', this._onButtonKey);
            this._buttons.push(a);
            li.appendChild(a);
            ul.appendChild(li);
            if (isSelected && !tabbingEnabled) {
                setAttributes(a, {
                    'tabindex': '0',
                    'aria-selected': 'true',
                    'data-selected': 'true'
                });
                tabbingEnabled = true;
            }
            // move title into panel
            panel.insertBefore(title, panel.firstChild);
            setAttributes(panel, {
                'aria-labelledby': labelId(i),
                'aria-hidden': bool2str(!(isSelected && !tabOpened && (tabOpened = true))),
                'id': tabId(i),
                'role': 'tabpanel',
                'tabindex': 0,
            });
        });
        this._element.insertBefore(ul, this._element.firstChild);
        if (!tabbingEnabled && this._buttons.length > 0) {
            this._buttons[0].setAttribute('tabindex', '0');
            this._buttons[0].dataset.selected = 'true';
        }
        if (!tabOpened) {
            this.activePanel = this._element.querySelector('[role=tabpanel]');
        }
        this._onHashChange();
    }

    _focusButton(currentId, nextIndex) {
        if (currentId !== null) {
            setAttributes(this._buttons[currentId], {
                'tabindex': '-1',
            });
        }
        setAttributes(this._buttons[nextIndex], {
            'tabindex': '0',
        });
        this._buttons[nextIndex].focus();
    }

    _focusPreviousButton(currentIndex) {
        const nextIndex = (currentIndex - 1 + this._buttons.length) % this._buttons.length;
        this._focusButton(currentIndex, nextIndex);
    }

    _focusNextButton(currentIndex) {
        const nextIndex = (currentIndex + 1) % this._buttons.length;
        this._focusButton(currentIndex, nextIndex);
    }

    /**
     * @param {MouseEvent} e
     * @private
     */
    _onButtonClick(e) {
        e.preventDefault();
        const a = e.currentTarget;
        this.activePanel = this._element.querySelector('#' + a.getAttribute('aria-controls'));
    }

    /**
     * @param {KeyboardEvent} e
     * @private
     */
    _onButtonKey(e) {
        const a = e.currentTarget;
        const currentTabId = this._buttons.indexOf(a);
        if (!e.ctrlKey) {
            switch (e.keyCode) {
                case KeyCode.UP:
                case KeyCode.LEFT:
                    this._focusPreviousButton(currentTabId);
                    e.preventDefault();
                    break;
                case KeyCode.DOWN:
                case KeyCode.RIGHT:
                    this._focusNextButton(currentTabId);
                    e.preventDefault();
                    break;
                case KeyCode.HOME:
                    this._focusButton(currentTabId, 0);
                    e.preventDefault();
                    break;
                case KeyCode.END:
                    this._focusButton(currentTabId, this._buttons.length - 1);
                    e.preventDefault();
                    break;
                case KeyCode.ENTER:
                case KeyCode.RETURN:
                case KeyCode.SPACE:
                    this.activePanel = this._element.querySelector('#' + a.getAttribute('aria-controls'));
                    e.preventDefault();
                    break;
            }
        }
    }

    /**
     * Reverts all changes made
     */
    destroy() {
        super.destroy();
        this._element.firstElementChild.remove();
        Array.from(this._element.querySelectorAll('.' + TABS_CLASS_CONTENT)).forEach(panel => {
            const title = panel.firstElementChild;
            panel.parentElement.insertBefore(title, panel);
            setAttributes(panel, {
                'aria-labelledby': null,
                'aria-hidden': null,
                'id': null,
                'role': null,
            });
        });
    }
}

export {
    TabPanel,
};
